import { html, nothing, unsafeCSS, type CSSResultGroup } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { ref } from 'lit/directives/ref.js';
import { LibElement, createUniqueId } from '../lib-element';
import { LibTab } from '../tab/lib-tab';
import '../icon/lib-icon';
import css from './lib-tabs.css';

/** An automatically activated tablist with optional content panels. */
@customElement('lib-tabs')
export class LibTabs extends LibElement {
  protected override get hostFocusSelector(): string { return '[role="tab"]'; }
  static override readonly styles: CSSResultGroup = [LibElement.hostStyles, unsafeCSS(css)];
  @property({ type: Number, attribute: 'active-index' }) activeIndex = 0;
  @property() position = 'top';
  @property() variant = 'underlined';
  @property({ attribute: 'scroll-mode' }) scrollMode = 'none';
  @property({ attribute: 'aria-label' }) label: string | null = null;
  @property({ type: Boolean }) managed = false;

  private readonly idPrefix = createUniqueId('lib-tabs');
  private readonly mutations = new MutationObserver(() => this.requestUpdate());
  private readonly forwardedAttributes = new WeakMap<Element, string[]>();
  private get items(): LibTab[] {
    return Array.from(this.children).filter((child): child is LibTab => child instanceof LibTab);
  }
  private tabId(item: LibTab) { return item.id || `${this.idPrefix}-tab-${this.items.indexOf(item)}`; }
  private get selectable() { return this.items.filter(item => !item.disabled && !item.invisible); }
  private get active() { return this.items[this.activeIndex]; }
  private get tabStop() {
    return this.selectable.includes(this.active) ? this.active : this.selectable[0];
  }
  private hasPanel(item: LibTab | undefined): boolean {
    return !!item && (this.managed ? item.hasPanel : Array.from(item.childNodes).some(node =>
      node.nodeType === Node.ELEMENT_NODE || !!node.textContent?.trim()));
  }

  constructor() {
    super();
    this.addEventListener('lib-tab-update', event => {
      if (event.target instanceof LibTab && event.target.parentElement === this) this.requestUpdate();
    });
  }

  override connectedCallback() {
    super.connectedCallback();
    this.mutations.observe(this, { childList: true, subtree: true, attributes: true, characterData: true });
    this.requestUpdate();
  }

  override disconnectedCallback() {
    this.mutations.disconnect();
    super.disconnectedCallback();
  }

  protected override updated() {
    for (const item of this.items) {
      const slot = item === this.active && this.hasPanel(item) ? 'panel' : '';
      if (item.slot !== slot) item.slot = slot;
    }
  }

  private async select(item: LibTab, focus = false) {
    if (item.disabled || item.invisible) return;
    const index = this.items.indexOf(item);
    if (index !== this.activeIndex) {
      this.activeIndex = index;
      this.emit('lib-tabs-change', { index });
    }
    if (focus) {
      await this.updateComplete;
      this.renderRoot.querySelector<HTMLButtonElement>(`#${CSS.escape(this.tabId(item))}`)?.focus();
    }
  }

  private keydown(event: KeyboardEvent, item: LibTab) {
    const next = this.position === 'left' ? 'ArrowDown' : 'ArrowRight';
    const previous = this.position === 'left' ? 'ArrowUp' : 'ArrowLeft';
    if (![next, previous, 'Home', 'End'].includes(event.key)) return;
    event.preventDefault();
      const items = this.selectable;
      if (!items.length) return;
      let index = (items.indexOf(item) + (event.key === next ? 1 : -1) + items.length) % items.length;
      if (event.key === 'Home') index = 0;
      else if (event.key === 'End') index = items.length - 1;
    void this.select(items[index], true);
  }

  private forwardAttributes(button: Element | undefined, item: LibTab) {
    if (!button) return;
    for (const name of this.forwardedAttributes.get(button) ?? []) button.removeAttribute(name);
    const attributes = Array.from(item.attributes).filter(attribute =>
      !['text', 'icon-url', 'badge-number', 'disabled', 'invisible', 'has-panel', 'class', 'style', 'slot',
        'id', 'role', 'tabindex', 'aria-selected', 'aria-controls'].includes(attribute.name)
      && !attribute.name.startsWith('_bl_'));
    for (const attribute of attributes) button.setAttribute(attribute.name, attribute.value);
    this.forwardedAttributes.set(button, attributes.map(attribute => attribute.name));
  }

  private renderTab(item: LibTab) {
    const index = this.items.indexOf(item);
    const active = index === this.activeIndex;
    return html`<button ${ref(button => this.forwardAttributes(button, item))} type="button"
      id=${this.tabId(item)} role="tab"
      class=${`lib-tab ${active ? 'lib-tab-active' : ''} ${item.disabled ? 'lib-tab-disabled' : ''} ${item.className}`}
      style=${item.getAttribute('style') ?? ''} ?disabled=${item.disabled}
      aria-selected=${String(active)} aria-controls=${this.hasPanel(item) ? `${this.idPrefix}-panel-${index}` : nothing}
      tabindex=${this.tabStop === item ? 0 : -1}
      @click=${() => this.select(item)} @keydown=${(event: KeyboardEvent) => this.keydown(event, item)}>
      ${item.iconUrl ? html`<lib-icon icon-url=${item.iconUrl}></lib-icon>` : nothing}
      ${item.text ? html`<span class="lib-tab-label">${item.text}</span>` : nothing}
      ${this.renderBadge(item.badgeNumber)}
    </button>`;
  }

  private renderBadge(number: number | null) {
    if (number === null) return nothing;
    const text = number > 9 ? '9+' : number;
    return html`<span class="lib-tab-badge">${text}</span>`;
  }

  override render() {
      const classes = `lib-tabs lib-tabs-${this.position} lib-tabs-${this.variant} ${this.scrollMode === 'auto' ? 'lib-tabs-scrollable' : ''}`;
      return html`<div class=${classes}>
      <slot class="metadata" @slotchange=${() => { this.requestUpdate(); this.emit('lib-tabs-order', { value: this.items.map(item => item.dataset.orderId || '').join(',') }); }}></slot>
      <div class="lib-tabs-tablist" role="tablist" aria-label=${this.label ?? nothing}
        aria-orientation=${this.position === 'left' ? 'vertical' : nothing}>
        ${repeat(this.items.filter(item => !item.invisible), item => item, item => this.renderTab(item))}
      </div>
      ${this.hasPanel(this.active) ? html`<div class="lib-tabs-content" role="tabpanel" tabindex="0"
        id=${`${this.idPrefix}-panel-${this.activeIndex}`} aria-labelledby=${this.tabId(this.active)}>
        <slot name="panel"></slot>
      </div>` : nothing}
    </div>`;
  }
}

declare global {
  interface HTMLElementTagNameMap { 'lib-tabs': LibTabs; }
}
